/**
 * SORT — N items into K labelled bins.
 *
 * Sibling of Ordering, and the whole difference is the key. Ordering's is a PERMUTATION of item
 * ids; Sort's is a PARTITION — which bin each item belongs in. Order matters there, membership
 * matters here, and holding that line is what forbids grading the sequence inside a bin by default:
 * a student with every bin right must not be marked wrong for the order they stacked them in.
 *
 * Everything else is Ordering's, deliberately: a flat `items[]` carrying text, formula or media,
 * the versions plumbing, branch conditions, scenario following. See `project_bin_sort_block` for
 * the settled design.
 */
import { activeFeedbackForBranch, resolveMatchedBranch } from './conditional-branch.js';
import { optionalOutcome, participationOutcome } from './grading-mode.js';
import { pickScenarioBranch } from './scenario.js';
import { addVersionWith, applyVersionHead, pickVersion, removeVersionWith, writeVersionWith, } from './versions.js';
/** An empty key, for a new block or a newly added version. */
export const emptySortKey = () => ({ placements: {} });
/** Which bin the key PUTS this item in, or null when it is not keyed yet. */
export function keyedBin(key, itemId) {
    for (const [binId, ids] of Object.entries(key.placements)) {
        if (ids.includes(itemId))
            return binId;
    }
    return null;
}
/**
 * Would this bin be accepted for this item?
 *
 * Three ways to pass: it is where the key puts it, the item accepts any bin, or the bin is one of
 * the item's extras. An item the key says nothing about accepts nothing — an unfinished key must
 * not quietly mark everything correct.
 */
export function acceptsBin(key, itemId, binId) {
    if (key.anyBin?.includes(itemId))
        return keyedBin(key, itemId) !== null;
    if (keyedBin(key, itemId) === binId)
        return true;
    return !!key.alsoAccepts?.[itemId]?.includes(binId);
}
/** Every bin this item would be accepted in, in `bins` order — what a feedback chip and the
 *  duplicate check both need to reason about. */
export function acceptedBins(key, bins, itemId) {
    return bins.map(b => b.id).filter(id => acceptsBin(key, itemId, id));
}
/** Items the key has not placed anywhere. A teacher's unfinished key and a student's unfinished
 *  attempt are the same question asked of two different partitions. */
export function unkeyedItems(key, items) {
    return items.filter(i => keyedBin(key, i.id) === null).map(i => i.id);
}
/**
 * The key with a deleted bin taken out of it — items RETURNED to unplaced, never deleted.
 *
 * Forced by versions sharing items: delete a bin in a two-version sort and there is no coherent
 * answer to "which items did that bin own", since the items in it in one version may be legitimately
 * placed elsewhere in the other, where the key is complete. So a bin never owns items, and deleting
 * one cannot take any with it. One behaviour, not two — a gesture whose meaning changed with the
 * number of versions would teach a teacher a rule that later stops being true.
 */
export function withBinRemoved(key, binId) {
    const placements = { ...key.placements };
    delete placements[binId];
    const alsoAccepts = key.alsoAccepts
        ? Object.fromEntries(Object.entries(key.alsoAccepts)
            .map(([itemId, binIds]) => [itemId, binIds.filter(id => id !== binId)])
            .filter(([, binIds]) => binIds.length > 0))
        : undefined;
    return {
        placements,
        ...(key.anyBin?.length ? { anyBin: key.anyBin } : {}),
        ...(alsoAccepts && Object.keys(alsoAccepts).length ? { alsoAccepts } : {}),
    };
}
/** The key with an item taken out of it — placements, the any-bin flag and the extras together, so
 *  a deleted item cannot leave a dangling id behind in any of the three. */
export function withItemRemoved(key, itemId) {
    const placements = Object.fromEntries(Object.entries(key.placements).map(([binId, ids]) => [binId, ids.filter(id => id !== itemId)]));
    const alsoAccepts = key.alsoAccepts && Object.fromEntries(Object.entries(key.alsoAccepts).filter(([id]) => id !== itemId));
    const anyBin = key.anyBin?.filter(id => id !== itemId);
    return {
        placements,
        ...(anyBin?.length ? { anyBin } : {}),
        ...(alsoAccepts && Object.keys(alsoAccepts).length ? { alsoAccepts } : {}),
    };
}
/** The key with one item moved into one bin, removed from wherever it was. `atIndex` inserts at a
 *  position so a drag can place between two items; appended when absent. */
export function withItemPlaced(key, itemId, binId, atIndex) {
    const cleared = withItemRemoved(key, itemId);
    const existing = cleared.placements[binId] ?? [];
    const next = [...existing];
    next.splice(atIndex ?? next.length, 0, itemId);
    return {
        ...cleared,
        placements: { ...cleared.placements, [binId]: next },
        // Moving an item does not revoke its tolerances; only deleting it does.
        ...(key.anyBin?.length ? { anyBin: key.anyBin } : {}),
        ...(key.alsoAccepts && Object.keys(key.alsoAccepts).length ? { alsoAccepts: key.alsoAccepts } : {}),
    };
}
/** A small FIXED set of structural mistake patterns, as Ordering's. Never a grid: an N x K matrix
 *  of per-item-per-bin states is a combo grid, which is ruled out directly. */
export const SORT_STRUCTURAL_TRIGGERS = ['all_in_one_bin', 'bins_swapped', 'one_misfiled'];
/**
 * WHAT A TEACHER MAY STILL AUTHOR, which is not the same as what the evaluator can still read.
 *
 * `all_in_one_bin` is RETIRED (owner, 2026-10-21): "a student tossed everything into one bin to be
 * done with it" turned out not to be a mistake worth writing feedback for. It stays in
 * `SORT_STRUCTURAL_TRIGGERS` and keeps matching, so a block that already has feedback keyed to it
 * goes on firing — the same way `no_response` was retired. It simply stops being offered.
 *
 * `bins_swapped` is offered at ANY bin count now. It used to be two-only, on the reasoning that
 * "with three there is no single opposite to swap into" — but the pattern a teacher wants is
 * "two bins exchanged, the rest correct", which is well defined for any number of them and is the
 * commonest near miss there is: the right grouping with the labels the wrong way round.
 */
export function availableSortStructuralTriggers(bins) {
    return bins.length >= 2 ? ['bins_swapped', 'one_misfiled'] : ['one_misfiled'];
}
const ORDER_TRIGGER_PREFIX = 'order:';
/** `order:*` — the sequence is wrong in ANY bin, which is what a teacher means when order matters
 *  everywhere and one message covers it. */
export const ORDER_ANY_BIN = '*';
/**
 * RIGHT ITEMS, WRONG ORDER — only reachable while the version grades order at all.
 *
 * The near miss that `one_misfiled` is for membership: everything is in the bin it belongs in and
 * the sequence inside is not the one authored. Without it that attempt falls to Default Incorrect
 * beside an attempt that put everything in one bin, and those are not the same mistake.
 *
 * Targeted at ONE bin, or at `ORDER_ANY_BIN` for a sort where order matters throughout.
 */
export function buildSortOrderTrigger(binId) {
    return `${ORDER_TRIGGER_PREFIX}${binId}`;
}
/** Returns null if `trigger` is not an order trigger. */
export function parseSortOrderTrigger(trigger) {
    if (!trigger.startsWith(ORDER_TRIGGER_PREFIX))
        return null;
    const rest = trigger.slice(ORDER_TRIGGER_PREFIX.length);
    return rest.length > 0 ? rest : null;
}
const ITEM_TRIGGER_PREFIX = 'item:';
const PARTITION_TRIGGER_PREFIX = 'partition:';
/**
 * "THIS item ended up in THAT bin" — a PARTIAL condition, and the whole reason Sort is a block.
 *
 * It says nothing about the rest of the arrangement, which is what makes *"you filed
 * 'civilizations are unstable' under Observations"* authorable. SPR graded the same activity as one
 * boolean from a widget and so could only ever say "wrong"; the misfiling is the teachable moment.
 *
 * Added on demand with two pickers. N x K never appears anywhere.
 */
export function buildSortItemTrigger(itemId, binId) {
    return `${ITEM_TRIGGER_PREFIX}${itemId}:${binId}`;
}
/** Returns null if `trigger` is not a per-item placement trigger. */
export function parseSortItemTrigger(trigger) {
    if (!trigger.startsWith(ITEM_TRIGGER_PREFIX))
        return null;
    const rest = trigger.slice(ITEM_TRIGGER_PREFIX.length);
    const cut = rest.lastIndexOf(':');
    if (cut <= 0 || cut === rest.length - 1)
        return null;
    return { itemId: rest.slice(0, cut), binId: rest.slice(cut + 1) };
}
/**
 * One whole authored wrong arrangement — the direct analogue of `buildCustomOrderTrigger`.
 *
 * MEMBERSHIP ONLY, canonicalised by sorting both bin ids and the item ids inside them. A teacher
 * may reorder the bins at any time and a student's within-bin sequence is not what this trigger is
 * about, so neither may change whether a stored trigger still matches the arrangement it was
 * authored against. Within-bin order is a separate axis, graded only by `orderWithinBins`.
 */
export function buildSortPartitionTrigger(placements) {
    const body = Object.entries(placements)
        .filter(([, ids]) => ids.length > 0)
        .map(([binId, ids]) => [binId, [...ids].sort()])
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([binId, ids]) => `${binId}=${ids.join(',')}`)
        .join(';');
    return `${PARTITION_TRIGGER_PREFIX}${body}`;
}
/** Returns null if `trigger` is not a custom-partition trigger. */
export function parseSortPartitionTrigger(trigger) {
    if (!trigger.startsWith(PARTITION_TRIGGER_PREFIX))
        return null;
    const rest = trigger.slice(PARTITION_TRIGGER_PREFIX.length);
    if (rest.length === 0)
        return {};
    const out = {};
    for (const group of rest.split(';')) {
        const cut = group.indexOf('=');
        if (cut <= 0)
            return null;
        out[group.slice(0, cut)] = group.slice(cut + 1).split(',');
    }
    return out;
}
/** Which bin the student put this item in, or null when they left it in the source. */
export function placedBin(placements, itemId) {
    for (const [binId, ids] of Object.entries(placements)) {
        if (ids.includes(itemId))
            return binId;
    }
    return null;
}
/**
 * An item the key will accept in more than one bin.
 *
 * ALWAYS-SATISFIED for the structural matchers. An either-item has no "opposite", so counting it
 * would let one legitimately-placed item defeat `bins_swapped`, and counting it as misfiled would
 * make `one_misfiled` fire on an arrangement that is in fact correct.
 */
function isEitherItem(key, bins, itemId) {
    return acceptedBins(key, bins, itemId).length > 1;
}
/** Every item placed somewhere the key accepts. The membership half of correctness. */
function allAccepted(key, placements, items) {
    return items.every(item => {
        const bin = placedBin(placements, item.id);
        return bin !== null && acceptsBin(key, item.id, bin);
    });
}
/**
 * The within-bin sequence, compared only over the items this bin OWNS in the key.
 *
 * An either-item has no canonical slot — the key does not say where in the stack it goes, only
 * that the bin is allowed — so it is lifted out of both sequences before they are compared. Grading
 * it by position would be grading something the teacher never authored.
 */
function orderMatches(key, bins, placements, onlyBin) {
    return bins.filter(b => onlyBin === undefined || b.id === onlyBin).every(bin => {
        const owns = (id) => keyedBin(key, id) === bin.id && !isEitherItem(key, bins, id);
        const authored = (key.placements[bin.id] ?? []).filter(owns);
        const submitted = (placements[bin.id] ?? []).filter(owns);
        return authored.length === submitted.length && authored.every((id, i) => submitted[i] === id);
    });
}
/** Every item has a bin in the key. An unfinished key must never mark an attempt correct, which is
 *  the same guard `canonical.length > 0` is in `evaluateOrdering`. */
function keyIsComplete(key, items) {
    return items.length > 0 && items.every(item => keyedBin(key, item.id) !== null);
}
/** Exported for the reachability pass, which enumerates arrangements against these same rules
 *  rather than reimplementing them. */
export function matchesSortTrigger(trigger, placements, key, items, bins) {
    const item = parseSortItemTrigger(trigger);
    if (item)
        return placedBin(placements, item.itemId) === item.binId;
    const partition = parseSortPartitionTrigger(trigger);
    if (partition)
        return buildSortPartitionTrigger(partition) === buildSortPartitionTrigger(placements);
    const placedIn = items.map(i => placedBin(placements, i.id));
    if (placedIn.some(bin => bin === null))
        return false;
    if (trigger === 'all_in_one_bin') {
        // Two items at minimum, or "all in one bin" is just where one item went. It must also be
        // WRONG — a two-bin sort whose key really does put everything in one bin is a correct answer,
        // and a targeted mistake state must never fire on one.
        if (items.length < 2 || bins.length < 2)
            return false;
        return new Set(placedIn).size === 1 && !allAccepted(key, placements, items);
    }
    if (trigger === 'bins_swapped') {
        /*
         * THE KEY WITH EXACTLY TWO BINS EXCHANGED, for any number of bins.
         *
         * Stated as a comparison of contents rather than as "every item is in the other bin", which is
         * what the two-bin version checked and what could not generalise. Two bins differ from the key
         * and each holds what the other should: everything else is exactly right. At two bins this is
         * the old behaviour, including the degenerate case where the key fills only one of them — an
         * empty bin exchanging with a full one is still an exchange.
         *
         * Either-items are skipped throughout for the reason `isEitherItem` records: an item the key
         * accepts in several bins cannot be out of place, so it can neither defeat a swap nor be one.
         */
        if (bins.length < 2)
            return false;
        const graded = items.filter(item => !isEitherItem(key, bins, item.id));
        if (graded.length === 0)
            return false;
        /* An unplaced item is not a swap — it is an unfinished attempt. */
        if (graded.some(item => placedBin(placements, item.id) === null))
            return false;
        if (graded.some(item => keyedBin(key, item.id) === null))
            return false;
        const keyedIn = (binId) => graded.filter(i => keyedBin(key, i.id) === binId).map(i => i.id).sort().join('\u0000');
        const placedInBin = (binId) => graded.filter(i => placedBin(placements, i.id) === binId).map(i => i.id).sort().join('\u0000');
        const differing = bins.filter(b => keyedIn(b.id) !== placedInBin(b.id));
        if (differing.length !== 2)
            return false;
        const [a, b] = differing;
        return keyedIn(a.id) === placedInBin(b.id) && keyedIn(b.id) === placedInBin(a.id);
    }
    const orderBin = parseSortOrderTrigger(trigger);
    if (orderBin !== null) {
        /* MEMBERSHIP MUST BE RIGHT FIRST. "Right items, wrong order" says exactly that, and firing it
           on an arrangement whose items are in the wrong bins would make it a second, vaguer
           `one_misfiled`. */
        if (!allAccepted(key, placements, items))
            return false;
        const target = orderBin === ORDER_ANY_BIN ? bins : bins.filter(b => b.id === orderBin);
        if (target.length === 0)
            return false;
        /* `orderMatches` over the WHOLE key decides correctness; this asks the same question of one
           bin, so the two cannot disagree about what a right order is. */
        return target.some(bin => !orderMatches(key, bins, placements, bin.id));
    }
    if (trigger === 'one_misfiled') {
        const wrong = items.filter(item => !acceptsBin(key, item.id, placedBin(placements, item.id)));
        return wrong.length === 1;
    }
    return false;
}
/**
 * The feedback states a student's arrangement fires.
 *
 * Evaluation sequence, the same shape as `evaluateFitb` — which is the right twin, because both
 * blocks have a genuine partial state that Ordering and MCQ do not:
 * 1. optional mode -> inert catch-all, whatever they did.
 * 2. nothing placed -> `unanswered`.
 * 3. something left in the source -> `incomplete_answer`. An "anywhere" item still has to be
 *    PLACED: any bin passes, but unplaced is still unfinished.
 * 4. survey mode -> `any_answer`.
 * 5. graded -> membership (and, when `orderWithinBins` is on, within-bin sequence) is checked
 *    first and is terminal; only when not correct is the targeted set consulted, every match
 *    firing in parallel, falling back to `incorrect`.
 *
 * FAILS CLOSED on a branch coverage gap, as every branching block must: the effective key is
 * `null`, which stands in as an empty key, and an empty key accepts no bin for any item — so
 * `correct` is unreachable and every structural matcher declines. A literal per-item or custom
 * partition trigger still fires, since each is matched against the student's own arrangement and
 * never against the key.
 */
export function evaluateSort(answer, blockData, resolvedVariables = {}, 
/** The leader's variant in force — see `evaluateDropdown`'s own note. */
scenarioId) {
    const { placements } = answer;
    const { items, bins, gradingMode, feedback, orderWithinBins, conditionalEquation, conditionalBranches, scenarioBranches, angleMode, } = blockData;
    /* A FOLLOWER'S SCENARIO ROW REPLACES the BLOCK's numeric branch, never joins it. */
    const followed = gradingMode === 'graded' && !!scenarioBranches && scenarioBranches.length > 0;
    const scenarioRow = followed ? pickScenarioBranch(scenarioBranches, scenarioId) : null;
    const matchedBranch = !followed && gradingMode === 'graded'
        ? resolveMatchedBranch(conditionalEquation, conditionalBranches, resolvedVariables, angleMode)
        : null;
    const effectiveKey = gradingMode !== 'graded' ? blockData.key
        : followed ? resolveScenarioKey(blockData.key, scenarioBranches, scenarioId)
            : resolveEffectiveKey(blockData.key, conditionalEquation, conditionalBranches, resolvedVariables, angleMode);
    const key = effectiveKey ?? emptySortKey();
    // Incomplete branch coverage falls back to the top-level set with targeted states STRIPPED, so an
    // unmatched answer lands on Default Incorrect instead of a leftover targeted state firing
    // terminally. See activeFeedbackForBranch.
    const activeFeedback = activeFeedbackForBranch((scenarioRow ?? matchedBranch)?.feedback, feedback, gradingMode === 'graded'
        && (followed || (!!conditionalBranches && conditionalBranches.length > 0)));
    /** ONE matcher, used by every grading mode — see `participationOutcome`. */
    const targetedForArrangement = () => activeFeedback.states.filter(st => st.type === 'targeted'
        && matchesSortTrigger(st.trigger, placements, key, items, bins));
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(activeFeedback.states);
    }
    const unplaced = items.filter(item => placedBin(placements, item.id) === null);
    if (unplaced.length === items.length) {
        const unanswered = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    if (unplaced.length > 0) {
        const incomplete = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'incomplete_answer');
        return incomplete ? [incomplete] : [];
    }
    if (gradingMode === 'survey') {
        return participationOutcome(activeFeedback.states, 'survey', targetedForArrangement());
    }
    const isCorrect = keyIsComplete(key, items)
        && allAccepted(key, placements, items)
        && (!orderWithinBins || orderMatches(key, bins, placements));
    if (isCorrect) {
        const correct = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
        return correct ? [correct] : [];
    }
    const matched = targetedForArrangement();
    if (matched.length > 0)
        return matched;
    const incorrect = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return incorrect ? [incorrect] : [];
}
/**
 * A follower's key for the scenario in force — the scenario twin of `resolveEffectiveKey`.
 *
 * `null` is the fail-closed answer: a linked block whose scenario has no row has no key, so no
 * arrangement can be correct. Returning the block's own key would mark a student against a scenario
 * they were never shown.
 */
export function resolveScenarioKey(base, branches, scenarioId) {
    if (!branches || branches.length === 0)
        return base;
    const matched = pickScenarioBranch(branches, scenarioId);
    return matched ? matched.key : null;
}
/**
 * The key in force for this roll. No branches -> the block's own, unchanged.
 *
 * Branches in use but nothing resolves -> `null`, deliberately NOT the block's own key. Once
 * branching is on, that key is only ever the SEED a new branch starts from; it stops being a
 * maintained "what is correct" the moment branches are the source of truth, so falling back to it
 * on a gap would resurrect content nobody is reviewing in the context of that failure.
 */
export function resolveEffectiveKey(base, equation, branches, resolvedVariables, angleMode = 'degrees') {
    if (!branches || branches.length === 0)
        return base;
    const matched = resolveMatchedBranch(equation, branches, resolvedVariables, angleMode);
    return matched ? matched.key : null;
}
/** A branch whose key can never grade correctly however the equation resolves — the Sort analogue
 *  of `orderBranchIssue`. An incomplete key is exactly the condition `keyIsComplete` rejects. */
export function sortBranchIssue(branch, items) {
    return keyIsComplete(branch.key, items) ? null : 'incomplete-key';
}
export function applySortVersion(data, versionId) {
    const version = pickVersion(data.versions, versionId);
    if (!version)
        return data;
    return { ...applyVersionHead(data, version), key: version.key, orderWithinBins: version.orderWithinBins };
}
export function writeSortVersion(current, versionId, next) {
    return writeVersionWith(current, versionId, next, { key: next.key, orderWithinBins: next.orderWithinBins });
}
export function addSortVersion(data, fromVersionId, newId) {
    return addVersionWith(data, fromVersionId, newId, { key: data.key, orderWithinBins: data.orderWithinBins });
}
export function removeSortVersion(data, versionId) {
    return removeVersionWith(data, versionId, applySortVersion);
}
/**
 * A key reconciled against the live item and bin sets — the same self-healing Ordering's
 * `reconcileOrder` does, for the same reason.
 *
 * A version, a branch or a stored key can outlive the items it names: an item deleted after the key
 * was authored leaves a dangling id, and a bin deleted leaves a whole dangling list. Neither should
 * render a phantom row or silently drop a newly added item from the picture.
 */
export function reconcileKey(key, items, bins) {
    const liveItems = new Set(items.map(i => i.id));
    const liveBins = new Set(bins.map(b => b.id));
    const placements = {};
    for (const bin of bins) {
        const ids = (key.placements[bin.id] ?? []).filter(id => liveItems.has(id));
        if (ids.length)
            placements[bin.id] = ids;
    }
    const anyBin = key.anyBin?.filter(id => liveItems.has(id));
    const alsoAccepts = key.alsoAccepts && Object.fromEntries(Object.entries(key.alsoAccepts)
        .filter(([itemId]) => liveItems.has(itemId))
        .map(([itemId, binIds]) => [itemId, binIds.filter(id => liveBins.has(id))])
        .filter(([, binIds]) => binIds.length > 0));
    return {
        placements,
        ...(anyBin?.length ? { anyBin } : {}),
        ...(alsoAccepts && Object.keys(alsoAccepts).length ? { alsoAccepts } : {}),
    };
}
//# sourceMappingURL=sort.js.map